import React, { useState, useEffect, useMemo, useRef } from 'react';
import HomeView from './home.view';
import { RegistrationFormValues, initialFormValues, RegistrationFormProps, RaceData, RaceTypeSummary } from './home.props';
import { useApiPost } from '../../../utils/apiservice';
import { useNavigate, useLocation } from 'react-router-dom';

import api from '../../../utils/apiservice';

export function useRegistrationForm(): RegistrationFormProps {
  const [values, setValues] = useState<RegistrationFormValues>(initialFormValues);
  const { post, loading: submitting, error: apiError } = useApiPost('/api/registrations');
  const [races, setRaces] = useState<RaceData[]>([]);
  const [racesLoading, setRacesLoading] = useState(true);
  const [error, setError] = useState<string | undefined>(undefined);
  const [tshirtSizes, setTshirtSizes] = useState<string[]>([]);
  const [retryDataLoaded, setRetryDataLoaded] = useState(false);
  const retryDataRef = useRef<any>(null);
  const navigate = useNavigate();
  const location = useLocation();

  // Set page title for public home page
  useEffect(() => {
    document.title = 'DrimRun';
  }, []);

  // Check for retry token in URL — fetch saved data for pre-fill
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const retryToken = params.get('retry');
    if (retryToken) {
      const loadRetryData = async () => {
        try {
          const res = await api.get(`/api/registrations/retry/${retryToken}`);
          retryDataRef.current = res.data;
          // If races already loaded, apply immediately
          if (!racesLoading) {
            applyRetryData(res.data);
          }
        } catch (err: any) {
          const msg = err.response?.data?.message || 'Retry link is invalid or expired.';
          setError(msg);
        }
      };
      loadRetryData();
    }
  }, [location.search]); // eslint-disable-line react-hooks/exhaustive-deps

  // Apply retry data once races finish loading
  useEffect(() => {
    if (!racesLoading && retryDataRef.current && !retryDataLoaded) {
      applyRetryData(retryDataRef.current);
    }
  }, [racesLoading]); // eslint-disable-line react-hooks/exhaustive-deps

  const applyRetryData = (data: any) => {
    setValues((prev: RegistrationFormValues) => ({
      ...prev,
      firstName: data.firstName || '',
      lastName: data.lastName || '',
      email: data.email || '',
      phone: data.phone || '',
      city: data.city || '',
      country: data.country || '',
      gender: data.gender || '',
      dateOfBirth: data.dateOfBirth ? data.dateOfBirth.split('T')[0] : '',
      raceId: data.raceId || '',
      raceTypeId: data.raceTypeId || '',
      participantType: data.participantType || 'Individual',
      teamName: data.teamName || '',
      tshirtSize: data.tshirtSize || 'M',
      discountCode: data.discountCode || '',
      emergencyContactPhone: data.emergencyContactPhone || '',
    }));
    setRetryDataLoaded(true);
    // Scroll to registration section after applying data
    setTimeout(() => {
      const section = document.getElementById('register');
      if (section) {
        const yOffset = -80;
        const y = section.getBoundingClientRect().top + window.pageYOffset + yOffset;
        window.scrollTo({ top: y, behavior: 'smooth' });
      }
    }, 600);
  };

  // Fetch config then races filtered by organizer
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        setRacesLoading(true);
        // Get the configured organizerId from the backend
        const configRes = await api.get('/api/config/public');
        const organizerId = configRes.data?.organizerId;
        const params: Record<string, string> = {};
        if (organizerId) params.organizerId = organizerId;
        const racesRes = await api.get('/api/races', { params });
        if (cancelled) return;
        const racesData: RaceData[] = racesRes.data || [];
        setRaces(racesData);
        // Auto-select the latest active race — but don't overwrite retry data
        if (racesData.length > 0) {
          const activeStatuses = ['upcoming', 'active', 'open'];
          const active = racesData.filter(r => !r.status || activeStatuses.includes(r.status.toLowerCase()));
          const pool = active.length > 0 ? active : racesData;
          const sorted = [...pool].sort((a, b) => new Date(b.startDate).getTime() - new Date(a.startDate).getTime());
          const bestRace = sorted[0];
          setValues((prev: RegistrationFormValues) => {
            // If raceId is already set (e.g. from retry), keep it
            if (prev.raceId) return prev;
            return { ...prev, raceId: bestRace.id, raceTypeId: '' };
          });
        }
      } catch (err) {
        console.error('Failed to load races:', err);
      } finally {
        if (!cancelled) setRacesLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Derive selected race and its race types
  const selectedRace = useMemo(() => {
    if (!races || !values.raceId) return null;
    return races.find((r: RaceData) => r.id === values.raceId) || null;
  }, [races, values.raceId]);

  const raceTypes = useMemo(() => {
    return selectedRace?.raceTypes || [];
  }, [selectedRace]);

  // Check if registration is currently open for the selected race
  const registrationOpen = useMemo(() => {
    if (!selectedRace) return false;
    const now = new Date();
    const open = selectedRace.openRegistrationDate ? new Date(selectedRace.openRegistrationDate) : null;
    const close = selectedRace.closeRegistrationDate ? new Date(selectedRace.closeRegistrationDate) : null;
    if (open && now < open) return false;
    if (close && now > close) return false;
    return true;
  }, [selectedRace]);

  // (Race auto-selection is handled in the initial fetch above)

  // Reset raceTypeId and fetch t-shirt sizes when raceId changes
  const raceIdRef = React.useRef(values.raceId);
  useEffect(() => {
    const currentRaceId = values.raceId;
    if (raceIdRef.current !== currentRaceId) {
      raceIdRef.current = currentRaceId;
      // Don't reset raceTypeId if retry data was just loaded
      if (!retryDataLoaded) {
        setValues((prev: RegistrationFormValues) => ({ ...prev, raceTypeId: '' }));
      }
    }
  }, [values.raceId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch t-shirt sizes only when both raceId AND raceTypeId are selected
  useEffect(() => {
    const currentRaceId = values.raceId;
    const currentRaceTypeId = values.raceTypeId;
    if (currentRaceId && currentRaceTypeId) {
      let cancelled = false;
      api.get(`/api/races/${currentRaceId}/tshirt-sizes`, { params: { raceTypeId: currentRaceTypeId } })
        .then((res: any) => {
          if (cancelled) return;
          const sizes = (res.data || []).sort((a: any, b: any) => a.sortOrder - b.sortOrder).map((s: any) => s.size);
          setTshirtSizes(sizes.length > 0 ? sizes : ['S', 'M', 'L', 'XL', 'XXL']);
          setValues((prev: RegistrationFormValues) => {
            if (sizes.length > 0 && !sizes.includes(prev.tshirtSize)) {
              return { ...prev, tshirtSize: sizes[0] };
            }
            return prev;
          });
        })
        .catch(() => {
          if (!cancelled) setTshirtSizes(['S', 'M', 'L', 'XL', 'XXL']);
        });
      return () => { cancelled = true; };
    } else {
      setTshirtSizes([]);
    }
  }, [values.raceId, values.raceTypeId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Get current language from localStorage for error translation
  function getLang() {
    const stored = localStorage.getItem('lang');
    if (stored === 'en' || stored === 'mk' || stored === 'al') return stored;
    return 'mk';
  }
  // Localized error messages
  const errorMessages = {
    en: require('../../../locales/en.json').reg.privacyPolicyRequired,
    mk: require('../../../locales/mk.json').reg.privacyPolicyRequired,
    al: require('../../../locales/al.json').reg.privacyPolicyRequired,
  };
  const cookiesErrorMessages = {
    en: require('../../../locales/en.json').reg.cookiesConsentRequired,
    mk: require('../../../locales/mk.json').reg.cookiesConsentRequired,
    al: require('../../../locales/al.json').reg.cookiesConsentRequired,
  };
  const emergencyPhoneErrorMessages = {
    en: require('../../../locales/en.json').reg.emergencyContactPhoneRequired,
    mk: require('../../../locales/mk.json').reg.emergencyContactPhoneRequired,
    al: require('../../../locales/al.json').reg.emergencyContactPhoneRequired,
  };

  const onChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setValues((prev: RegistrationFormValues) => ({ ...prev, [name]: value }));
  };

  const onCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, checked } = e.target;
    setValues((prev: RegistrationFormValues) => ({ ...prev, [name]: checked }));
  };

  const onSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError(undefined);
    if (!values.cookiesConsent || !values.privacyPolicy) {
      const lang = getLang();
      if (!values.cookiesConsent) {
        setError(cookiesErrorMessages[lang]);
      } else {
        setError(errorMessages[lang]);
      }
      return;
    }
    if (!values.emergencyContactPhone || !values.emergencyContactPhone.trim()) {
      const lang = getLang();
      setError(emergencyPhoneErrorMessages[lang]);
      return;
    }
    if (!values.raceId || !values.raceTypeId) {
      setError('Please select a race and race type.');
      return;
    }
    // Build payload matching RegistrationCreateDto
    const payload = {
      firstName: values.firstName,
      lastName: values.lastName,
      email: values.email,
      phone: values.phone,
      city: values.city,
      country: values.country,
      gender: values.gender,
      dateOfBirth: values.dateOfBirth,
      participantType: values.participantType,
      teamName: values.teamName || '',
      tshirtSize: values.tshirtSize,
      discountCode: values.discountCode || '',
      emergencyContactPhone: values.emergencyContactPhone,
      cookiesConsent: values.cookiesConsent,
      privacyPolicy: values.privacyPolicy,
      raceId: values.raceId,
      raceTypeId: Number(values.raceTypeId),
    };

    const result = await post(
      payload,
      undefined,
      {
        onSuccess: async (result) => {
          if (result && (result as any).orderNumber && (result as any).paymentData) {
            const paymentData = (result as any).paymentData;
            const form = document.createElement('form');
            form.method = 'POST';
            form.action = 'https://vpos.cpay.com.mk/mk-MK/';
            form.innerHTML = `
              <input name="isSimple" value="true" type="hidden" />
              <input name="AmountToPay" value="${paymentData.amountToPay}" type="hidden" />
              <input name="PayToMerchant" value="${paymentData.payToMerchant}" type="hidden" />
              <input name="MerchantName" value="${paymentData.merchantName}" type="hidden" />
              <input name="AmountCurrency" value="${paymentData.amountCurrency}" type="hidden" />
              <input name="Details1" value="${paymentData.details1}" type="hidden" />
              <input name="Details2" value="${paymentData.details2}" type="hidden" />
              <input name="PaymentOKURL" value="${paymentData.paymentOKURL}" type="hidden" />
              <input name="PaymentFailURL" value="${paymentData.paymentFailURL}" type="hidden" />
              <input name="CheckSumHeader" value="${paymentData.checkSumHeader}" type="hidden" />
              <input name="CheckSum" value="${paymentData.checkSum}" type="hidden" />
              <input name="FirstName" value="${paymentData.firstName}" type="hidden" />
              <input name="LastName" value="${paymentData.lastName}" type="hidden" />
            `;
            document.body.appendChild(form);
            form.submit();
          } else if ((result as any).amount === 0) {
            navigate(`/success-registration?Details2=${(result as any).orderNumber}`);
          } else {
            // Registration created successfully (standard flow)
            navigate(`/success-registration?id=${(result as any).id}`);
          }
        },
        onError: (err) => {
          setError('Registration failed. Please try again.');
        }
      }
    );
  };

  return {
    values,
    onChange,
    onCheckboxChange,
    onSubmit,
    submitting,
    error: error || (apiError ? 'Registration failed. Please try again.' : undefined),
    races: races || [],
    racesLoading,
    selectedRace,
    raceTypes,
    registrationOpen,
    tshirtSizes,
  };
}

const Home = (props: any) => {
  // Use the registration form hook to get form state and handlers
  const formProps = useRegistrationForm();
  // Pass all props and formProps to HomeView
  return <HomeView {...props} {...formProps} />;
};

export { HomeView };
export default Home;
